import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { IInvoiceCycle } from '../../contract-management/IInvoiceCycle';
import { Guid, IGenericResponse } from '../../shared';
import { LoadingService } from '../../shared/loading';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromRoot from '../../store';
import * as fromStore from './store';

@Injectable({
    providedIn: 'root'
})
export class InvoiceManagementService extends LoadingService {
    private uri = `${environment.apiLocation}/ContractInvoiceMgmt`;
    private uriActiveContracts = `${environment.apiLocation}/ContractwithInvoiceActivity`;
    private uriSession = `${environment.apiLocation}/InvoiceVoucherNbr`;
    private uriChargeNumber = `${environment.apiLocation}/CNInvoiceMgmt`;
    private uriInvoiceCycle = `${environment.apiLocation}/InvoiceCycle`;

    constructor(
        private http: HttpClient,
        protected log: LoggingService,
        protected rootStore: Store<fromRoot.CoreState>
    ) {
        super(rootStore);
    }
    get(): Observable<fromStore.Invoice.Invoice[]> {
        return this.http
            .get<fromStore.Invoice.Invoice[]>(this.uri)
            .pipe(map(res => this.mapInvoices(res)));
    }
    post(
        startDate?: Date,
        endDate?: Date,
        contractList?: string[],
        showInactive: boolean = false,
        showInvoicesOnly: boolean = false,
        resetInvoiceSchedule: boolean = false
    ): Observable<fromStore.Invoice.Invoice[]> {
        super.Load();
        const params = {
            Activeonly: !showInactive,
            InvoicesOnly: showInvoicesOnly,
            ResetInvoiceSchedule: resetInvoiceSchedule
        } as any;
        if (!!startDate) {
            params.ScheduledStartDate = moment(startDate).format('MM/DD/YYYY');
        }
        if (!!endDate) {
            params.ScheduledEndDate = moment(endDate).format('MM/DD/YYYY');
        }
        if (!!contractList && contractList.length > 0) {
            params.ContractList = contractList.map(x => ({
                ContractListID: x,
                ContractDesc: ''
            }));
        }
        this.log.verbose({
            type: 'InvoiceManagementService:post',
            startDate,
            endDate,
            contractList,
            params
        });
        return this.http
            .post<fromStore.Invoice.Invoice[]>(this.uri, params)
            .pipe(
                tap(() => super.Stop()),
                map(res => this.mapInvoices(res))
            );
    }
    put(
        Invoices: fromStore.Invoice.Invoice[]
    ): Observable<IGenericResponse<fromStore.Invoice.Invoice>> {
        return this.http
            .put<IGenericResponse<fromStore.Invoice.Invoice>>(this.uri, {
                ContractInvoiceMgmtList: Invoices
            })
            .pipe(
                map(res => ({
                    ...res,
                    Items: this.mapInvoices(res.Items)
                }))
            );
    }
    getInvoiceSessions(
        ScheduledStartDate: Date,
        ScheduledEndDate: Date,
        ContractListID: string[],
        ContractDesc: string[],
        // showInactive: boolean = false,
        showInvoicesOnly: boolean = false,
        DateRangeView?: boolean
    ): Observable<fromStore.SessionNumber.InvoiceSessionNumber[]> {
        this.log.verbose({
            type: 'InvoiceManagementService:getInvoiceSessions',
            ScheduledStartDate,
            ScheduledEndDate,
            ContractListID,
            ContractDesc,
            // showInactive,
            showInvoicesOnly,
            DateRangeView
        });
        let contractList: Array<{
            ContractListID: string;
            ContractDesc: string;
        }> = [];
        if (ContractListID && ContractListID.length > 0) {
            // contractList: Array<{ContractListID: string, ContractDesc: string}> = [];
            for (let i = 0; i < ContractListID.length; i++) {
                contractList.push({
                    ContractListID: ContractListID[i],
                    ContractDesc: ''
                });
            }
        } else {
            contractList = [];
        }

        return this.http
            .post<fromStore.SessionNumber.InvoiceSessionNumber[]>(
                this.uriSession,
                {
                    ScheduledStartDate,
                    ScheduledEndDate,
                    ContractList: contractList,
                    // Activeonly: !showInactive,
                    Invoicesonly: showInvoicesOnly,
                    DateRangeView: DateRangeView
                }
            )
            .pipe(
                map(x =>
                    x.map(y => ({
                        ...y,
                        id: y.Ib_id,
                        SessionEnd: moment(y.SessionEnd).format('MM/DD/YYYY'),
                        SessionStart: y.SessionStart
                    }))
                )
            );
    }
    putInvoiceSessions(
        sessions: fromStore.SessionNumber.InvoiceSessionNumber[]
    ) {
        return this.http
            .put<
                IGenericResponse<fromStore.SessionNumber.InvoiceSessionNumber>
            >(this.uriSession, {
                InvoiceVoucherNbrList: sessions
            })
            .pipe(
                map(res => ({
                    ...res,
                    Items: res.Items.map(y => ({
                        ...y,
                        id: y.Ib_id
                    }))
                }))
            );
    }
    public deleteInvoiceSessions(
        ids: Array<number>
    ): Observable<
        IGenericResponse<fromStore.SessionNumber.InvoiceSessionNumber>
    > {
        return (
            this.http
                // TODO: change to .delete<IResponse> when upgrading to NG7
                .request<
                    IGenericResponse<fromStore.SessionNumber.InvoiceSessionNumber>
                >('DELETE', this.uriSession, {
                    body: { EntityIDs: ids }
                })
                .pipe(
                    map(resp => ({
                        ...resp,
                        Items: resp.Items
                    })),
                    tap(data =>
                        this.log.verbose({
                            type: 'InvoiceManagmentService:deleteInvoiceSessions:response',
                            data
                        })
                    )
                )
        );
    }
    getChargeNumbers(
        ScheduledStartDate: Date,
        ScheduledEndDate: Date,
        ContractListID: string,
        ContractDesc: string,
        showInactive: boolean = false
    ): Observable<fromStore.ChargeNumber.InvoiceChargeNumber[]> {
        return this.http
            .post<fromStore.ChargeNumber.InvoiceChargeNumber[]>(
                this.uriChargeNumber,
                {
                    ScheduledStartDate,
                    ScheduledEndDate,
                    ContractList: [{ ContractListID, ContractDesc }],
                    Activeonly: !showInactive
                }
            )
            .pipe(
                map(x =>
                    x.map(y => ({
                        ...y,
                        id: this.getChargeNumberId(y)
                    }))
                )
            );
    }
    getActiveContract(
        startDate: Date,
        endDate: Date,
        showInactive: boolean
    ): Observable<{ ContractListID: string; ContractDesc: string }[]> {
        return this.http.post<
            { ContractListID: string; ContractDesc: string }[]
        >(this.uriActiveContracts, {
            StartDate: moment(startDate).format('MM/DD/YYYY'),
            EndDate: moment(endDate).format('MM/DD/YYYY'),
            Activeonly: !showInactive
        });
    }
    mapInvoices(
        invoices: fromStore.Invoice.Invoice[]
    ): fromStore.Invoice.Invoice[] {
        return invoices.map(x => ({
            id: this.getInvoiceId(x),
            ...x,
            InvoiceDate: x.InvoiceDate ? moment(x.InvoiceDate).toDate() : null,
            InvoiceEnd: x.InvoiceEnd ? moment(x.InvoiceEnd).toDate() : null,
            InvoiceStart: x.InvoiceStart
                ? moment(x.InvoiceStart).toDate()
                : null,
            LastModifiedDate: x.LastModifiedDate
                ? moment(x.LastModifiedDate).toDate()
                : null,
            ScheduledEndDate: x.ScheduledEndDate
                ? moment(x.ScheduledEndDate).toDate()
                : null,
            ScheduledStartDate: x.ScheduledStartDate
                ? moment(x.ScheduledStartDate).toDate()
                : null,
            ScheduledEndOverride: x.ScheduledEndOverride
                ? moment(x.ScheduledEndOverride).toDate()
                : null,
            ScheduledStartOverride: x.ScheduledStartOverride
                ? moment(x.ScheduledStartOverride).toDate()
                : null
        }));
    }
    private getChargeNumberId(
        item: fromStore.ChargeNumber.InvoiceChargeNumber
    ) {
        return item.Uniqueid;
    }
    private getInvoiceId(item: fromStore.Invoice.Invoice) {
        return item.Uniqueid || Guid.newGuid();
    }
    putInvoiceCycle(
        item: IInvoiceCycle
    ): Observable<IGenericResponse<IInvoiceCycle>> {
        return this.http
            .put<IGenericResponse<IInvoiceCycle>>(this.uriInvoiceCycle, {
                ...item
            })
            .pipe
            // map(res => ({
            //     ...res,
            //     Items: this.mapInvoices(res.Items)
            // }))
            ();
    }
}
